import React, { useState, useMemo, useRef, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { Project } from '../../types/project';
import { Task } from '../../types/task';
import { useTasks } from '../../hooks/useTasks';
import { useProjects } from '../../hooks/useProjects';
import TaskForm from '../tasks/TaskForm';
import ProjectForm from './ProjectForm';
import { 
  GanttTask, 
  GanttViewSettings, 
  GANTT_ROW_HEIGHT, 
  GANTT_HEADER_HEIGHT,
  GANTT_SIDEBAR_WIDTH,
  GANTT_COLORS,
  convertTaskToGanttTask,
  convertProjectToGanttTask
} from '../../types/gantt';
import {
  calculateDateRange,
  generateTimelineColumns,
  getColumnWidth,
  calculateTaskBarPosition,
  isTaskOverdue,
  calculateTaskDuration,
  formatDateRange,
  formatDuration,
  getStatusLabel,
  sortTasksByDate
} from '../../utils/ganttHelpers';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  FlagIcon,
  ArrowsPointingOutIcon,
  ArrowsPointingInIcon
} from '@heroicons/react/24/outline';

interface MultiProjectGanttViewProps {
  // No props - shows all active projects
}

const MultiProjectGanttView: React.FC<MultiProjectGanttViewProps> = () => {
  const { tasks, loading: tasksLoading, updateTask } = useTasks();
  const { projects, loading: projectsLoading, updateProject } = useProjects();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Dark mode detection
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  // Fullscreen mode & responsive
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('dark'));
    };
    
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  // Check for mobile and auto-enable fullscreen
  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setIsFullscreen(true); // Auto fullscreen on mobile
      }
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // View settings
  const [settings, setSettings] = useState<GanttViewSettings>({
    timeScale: 'week',
    showWeekends: true,
    showToday: true,
    showDependencies: false,
    showProgress: true,
    showCriticalPath: false,
    zoomLevel: 3,
    groupBy: 'none'
  });

  // Collapsed state for projects
  const [collapsedProjects, setCollapsedProjects] = useState<Set<string>>(new Set());
  
  // Tooltip state
  const [hoveredTask, setHoveredTask] = useState<{ task: GanttTask; x: number; y: number } | null>(null);
  
  // Task edit modal state
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  
  // Project edit modal state
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [showProjectForm, setShowProjectForm] = useState(false);

  // Convert all projects and tasks to Gantt format
  const ganttTasks = useMemo(() => {
    const activeProjects = projects.filter(p => p.isActive && !p.isArchived);
    const allGanttTasks: GanttTask[] = [];

    activeProjects.forEach(project => {
      // Add project as top-level task
      const projectTask = convertProjectToGanttTask(project);
      allGanttTasks.push(projectTask);

      // Add project's tasks if not collapsed
      if (!collapsedProjects.has(project.id)) {
        const projectTasks = tasks
          .filter(task => task.projectId === project.id)
          .map(convertTaskToGanttTask)
          .filter((task): task is GanttTask => task !== null);
        
        allGanttTasks.push(...sortTasksByDate(projectTasks));
      }
    });

    return allGanttTasks;
  }, [tasks, projects, collapsedProjects]);

  // Calculate date range and timeline
  const { start: startDate, end: endDate } = useMemo(
    () => calculateDateRange(ganttTasks, settings),
    [ganttTasks, settings]
  );

  const timelineColumns = useMemo(
    () => generateTimelineColumns(startDate, endDate, settings.timeScale),
    [startDate, endDate, settings.timeScale]
  );

  const columnWidth = getColumnWidth(settings.timeScale);
  const totalWidth = timelineColumns.length * columnWidth;

  // Toggle project collapse
  const toggleProjectCollapse = (projectId: string) => {
    setCollapsedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  // Scroll to today on mount
  useEffect(() => {
    if (scrollContainerRef.current && settings.showToday) {
      const todayIndex = timelineColumns.findIndex(col => col.isToday);
      if (todayIndex !== -1) {
        const scrollPosition = todayIndex * columnWidth - 200;
        scrollContainerRef.current.scrollLeft = Math.max(0, scrollPosition);
      }
    }
  }, [timelineColumns, columnWidth, settings.showToday]);

  const loading = tasksLoading || projectsLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className={isDarkMode ? "text-gray-400" : "text-gray-500"}>Betöltés...</div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Toolbar */}
      <div className={`mb-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 p-3 md:p-4 rounded-lg ${
        isDarkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
      }`}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <CalendarIcon className={`h-4 w-4 md:h-5 md:w-5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`} />
            <span className={`text-xs md:text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
              {formatDateRange(startDate, endDate)}
            </span>
          </div>
          <div className={`text-xs md:text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            {projects.filter(p => p.isActive && !p.isArchived).length} projekt, {ganttTasks.filter(t => t.type !== 'project').length} feladat
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
          {/* Time Scale Selector */}
          <select
            value={settings.timeScale}
            onChange={(e) => setSettings(prev => ({ 
              ...prev, 
              timeScale: e.target.value as GanttViewSettings['timeScale'] 
            }))}
            className={`px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-gray-200' 
                : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            <option value="day">Napi</option>
            <option value="week">Heti</option>
            <option value="month">Havi</option>
            <option value="quarter">Negyedéves</option>
          </select>

          {/* Show Today Toggle */}
          <button
            onClick={() => setSettings(prev => ({ ...prev, showToday: !prev.showToday }))}
            className={`px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm rounded-md transition-colors ${
              settings.showToday
                ? 'bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-600'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Ma
          </button>

          {/* Show Progress Toggle */}
          <button
            onClick={() => setSettings(prev => ({ ...prev, showProgress: !prev.showProgress }))}
            className={`px-2 md:px-3 py-1 md:py-1.5 text-xs md:text-sm rounded-md transition-colors ${
              settings.showProgress
                ? 'bg-purple-100 text-purple-700 border border-purple-300 dark:bg-purple-900 dark:text-purple-300 dark:border-purple-700'
                : isDarkMode
                  ? 'bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-600'
                  : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
          >
            Haladás
          </button>

          {/* Fullscreen Toggle - Hidden on mobile */}
          {!isMobile && (
          <button
            onClick={() => setIsFullscreen(!isFullscreen)}
            className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
              isDarkMode
                ? 'bg-gray-700 text-gray-200 border border-gray-600 hover:bg-gray-600'
                : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
            }`}
            title={isFullscreen ? 'Kilépés a teljes képernyőből' : 'Teljes képernyő'}
          >
            {isFullscreen ? (
              <ArrowsPointingInIcon className="h-4 w-4" />
            ) : (
              <ArrowsPointingOutIcon className="h-4 w-4" />
            )}
          </button>
          )}
        </div>
      </div>

      {/* Gantt Chart */}
      <div className={`flex-1 border rounded-lg overflow-hidden ${
        isDarkMode 
          ? 'border-gray-800 bg-gray-900' 
          : 'border-gray-200 bg-white'
      }`}>
        <div className="flex h-full">
          {/* Left Sidebar - Task List */}
          {!isFullscreen && (
          <div 
            className={`flex-shrink-0 border-r ${
              isDarkMode 
                ? 'border-gray-800 bg-gray-900' 
                : 'border-gray-200 bg-gray-50'
            }`}
            style={{ width: GANTT_SIDEBAR_WIDTH }}
          >
            {/* Header */}
            <div 
              className={`border-b px-4 flex items-center font-semibold ${
                isDarkMode 
                  ? 'border-gray-800 bg-gray-800 text-gray-200' 
                  : 'border-gray-200 bg-white text-gray-700'
              }`}
              style={{ height: GANTT_HEADER_HEIGHT }}
            >
              Projekt / Feladat
            </div>

            {/* Task Rows */}
            <div className="overflow-y-auto" style={{ height: `calc(100% - ${GANTT_HEADER_HEIGHT}px)` }}>
              {ganttTasks.map((task, index) => (
                <div
                  key={task.id}
                  className={`border-b px-4 flex items-center ${
                    isDarkMode
                      ? task.type === 'project' 
                        ? 'bg-purple-900/20 border-gray-800' 
                        : 'bg-gray-900 hover:bg-gray-800 border-gray-800'
                      : task.type === 'project' 
                        ? 'bg-purple-50 border-gray-200' 
                        : 'bg-white hover:bg-gray-50 border-gray-200'
                  }`}
                  style={{ 
                    height: GANTT_ROW_HEIGHT,
                    paddingLeft: task.level ? `${task.level * 20 + 16}px` : '16px'
                  }}
                >
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    {/* Collapse toggle for project */}
                    {task.type === 'project' && (
                      <button
                        onClick={() => toggleProjectCollapse(task.id)}
                        className={`flex-shrink-0 ${
                          isDarkMode 
                            ? 'text-gray-500 hover:text-gray-300' 
                            : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {collapsedProjects.has(task.id) ? (
                          <ChevronRightIcon className="h-4 w-4" />
                        ) : (
                          <ChevronDownIcon className="h-4 w-4" />
                        )}
                      </button>
                    )}

                    {/* Task name */}
                    <div className="flex-1 min-w-0">
                      <div className={`text-sm truncate ${
                        task.type === 'project' 
                          ? isDarkMode 
                            ? 'font-semibold text-purple-400' 
                            : 'font-semibold text-purple-700'
                          : isDarkMode 
                            ? 'text-gray-200' 
                            : 'text-gray-900'
                      }`}>
                        {task.name}
                      </div>
                      {task.assignedToName && task.type !== 'project' && (
                        <div className={`text-xs truncate ${
                          isDarkMode ? 'text-gray-400' : 'text-gray-500'
                        }`}>
                          {task.assignedToName}
                        </div>
                      )}
                    </div>

                    {/* Priority badge */}
                    {task.type !== 'project' && (
                      <div className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${
                        task.priority === 'urgent' ? 'bg-red-100 text-red-700' :
                        task.priority === 'high' ? 'bg-orange-100 text-orange-700' :
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-green-100 text-green-700'
                      }`}>
                        {task.priority === 'urgent' ? 'Sürgős' :
                         task.priority === 'high' ? 'Magas' :
                         task.priority === 'medium' ? 'Közepes' : 'Alacsony'}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
          )}

          {/* Right Side - Timeline */}
          <div className="flex-1 overflow-x-auto" ref={scrollContainerRef}>
            <div style={{ width: totalWidth, minWidth: '100%' }}>
              {/* Timeline Header */}
              <div 
                className={`border-b flex ${
                  isDarkMode 
                    ? 'border-gray-800 bg-gray-800' 
                    : 'border-gray-200 bg-white'
                }`}
                style={{ height: GANTT_HEADER_HEIGHT }}
              >
                {timelineColumns.map((column, index) => (
                  <div
                    key={index}
                    className={`border-r flex items-center justify-center text-xs font-medium ${
                      column.isToday 
                        ? isDarkMode
                          ? 'bg-purple-900/30 text-purple-300 border-gray-700'
                          : 'bg-purple-50 text-purple-700 border-gray-200'
                        : column.isWeekend && settings.showWeekends 
                          ? isDarkMode
                            ? 'bg-gray-900 text-gray-500 border-gray-800'
                            : 'bg-gray-100 text-gray-500 border-gray-200'
                          : isDarkMode
                            ? 'text-gray-300 border-gray-800'
                            : 'text-gray-700 border-gray-200'
                    }`}
                    style={{ width: columnWidth }}
                  >
                    {column.label}
                  </div>
                ))}
              </div>

              {/* Timeline Grid */}
              <div className="relative" style={{ height: ganttTasks.length * GANTT_ROW_HEIGHT }}>
                {/* Grid columns */}
                {timelineColumns.map((column, index) => (
                  <div
                    key={index}
                    className={`absolute top-0 bottom-0 border-r ${
                      column.isToday && settings.showToday 
                        ? isDarkMode
                          ? 'border-purple-600 bg-purple-900/20'
                          : 'border-purple-400 bg-purple-50/30'
                        : column.isWeekend && settings.showWeekends 
                          ? isDarkMode
                            ? 'bg-gray-900 border-gray-800'
                            : 'bg-gray-50 border-gray-200'
                          : isDarkMode
                            ? 'border-gray-800'
                            : 'border-gray-200'
                    }`}
                    style={{ 
                      left: index * columnWidth,
                      width: columnWidth
                    }}
                  />
                ))}

                {/* Grid rows */}
                {ganttTasks.map((_, index) => (
                  <div
                    key={index}
                    className={`absolute left-0 right-0 border-b ${
                      isDarkMode ? 'border-gray-800' : 'border-gray-200'
                    }`}
                    style={{ 
                      top: index * GANTT_ROW_HEIGHT,
                      height: GANTT_ROW_HEIGHT
                    }}
                  />
                ))}

                {/* Task bars */}
                {ganttTasks.map((task, index) => {
                  const { x, width } = calculateTaskBarPosition(task, startDate, settings.timeScale);
                  const overdue = isTaskOverdue(task);
                  const duration = calculateTaskDuration(task);

                  return (
                    <div
                      key={task.id}
                      className="absolute"
                      style={{
                        left: x,
                        top: index * GANTT_ROW_HEIGHT + 8,
                        width: width,
                        height: GANTT_ROW_HEIGHT - 16
                      }}
                      onMouseEnter={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        setHoveredTask({ task, x: rect.left, y: rect.top });
                      }}
                      onMouseLeave={() => setHoveredTask(null)}
                      onClick={() => {
                        if (task.type === 'project') {
                          // Open project edit modal
                          const project = projects.find(p => p.id === task.id);
                          if (project) {
                            setEditingProject(project);
                            setShowProjectForm(true);
                          }
                        } else if (task.taskId) {
                          // Open task edit modal
                          const originalTask = tasks.find(t => t.id === task.taskId);
                          if (originalTask) {
                            setEditingTask(originalTask);
                            setShowTaskForm(true);
                          }
                        }
                      }}
                    >
                      {/* Task bar */}
                      <div
                        className={`h-full rounded-md shadow-sm transition-all hover:shadow-md cursor-pointer ${
                          overdue ? 'ring-2 ring-red-500' : ''
                        }`}
                        style={{ 
                          backgroundColor: task.color || GANTT_COLORS[task.status],
                          opacity: task.type === 'project' ? 0.8 : 1
                        }}
                      >
                        {/* Progress bar */}
                        {settings.showProgress && task.progress > 0 && (
                          <div
                            className="h-full rounded-md bg-black/20"
                            style={{ width: `${task.progress}%` }}
                          />
                        )}

                        {/* Task label */}
                        <div className="absolute inset-0 flex items-center px-2">
                          <span className="text-xs font-medium text-white truncate">
                            {task.name}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className={`mt-4 flex items-center gap-6 text-xs ${
        isDarkMode ? 'text-gray-400' : 'text-gray-600'
      }`}>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gray-500" />
          <span>Tennivaló</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-500" />
          <span>Folyamatban</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-500" />
          <span>Kész</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-500" />
          <span>Megszakítva</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-purple-600" />
          <span>Projekt</span>
        </div>
      </div>

      {/* Fixed Tooltip - outside of scroll container */}
      {hoveredTask && (
        <div
          className="fixed z-[9999] pointer-events-none animate-in fade-in duration-300"
          style={{
            left: hoveredTask.x,
            top: hoveredTask.y - 10,
            transform: 'translateY(-100%)'
          }}
        >
          <div className="bg-gray-900 text-white text-xs rounded-lg shadow-xl p-3 min-w-[220px] max-w-[300px]">
            <div className="font-semibold mb-2 text-sm">{hoveredTask.task.name}</div>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="text-xs">{formatDateRange(hoveredTask.task.start, hoveredTask.task.end)}</span>
              </div>
              <div className="flex items-center gap-2">
                <ClockIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="text-xs">{formatDuration(calculateTaskDuration(hoveredTask.task))}</span>
              </div>
              {hoveredTask.task.assignedToName && (
                <div className="flex items-center gap-2">
                  <UserIcon className="h-3.5 w-3.5 flex-shrink-0" />
                  <span className="text-xs">{hoveredTask.task.assignedToName}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <FlagIcon className="h-3.5 w-3.5 flex-shrink-0" />
                <span className="text-xs">{getStatusLabel(hoveredTask.task.status)} - {hoveredTask.task.progress}%</span>
              </div>
              {isTaskOverdue(hoveredTask.task) && (
                <div className="text-red-400 font-medium mt-2 text-xs">
                  ⚠️ Lejárt határidő
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Task Edit Modal */}
      <TaskForm
        isOpen={showTaskForm}
        onClose={() => {
          setShowTaskForm(false);
          setEditingTask(null);
        }}
        onSubmit={async (data) => {
          try {
            if (editingTask) {
              await updateTask(editingTask.id, data);
              toast.success('Feladat frissítve!');
            }
            setShowTaskForm(false);
            setEditingTask(null);
          } catch (error) {
            console.error('Error updating task:', error);
            toast.error('Hiba a feladat frissítése során');
          }
        }}
        initialData={editingTask || undefined}
      />

      {/* Project Edit Modal */}
      <ProjectForm
        isOpen={showProjectForm}
        onClose={() => {
          setShowProjectForm(false);
          setEditingProject(null);
        }}
        onSubmit={async (data) => {
          try {
            if (editingProject) {
              await updateProject(editingProject.id, data);
              toast.success('Projekt frissítve!');
            }
            setShowProjectForm(false);
            setEditingProject(null);
          } catch (error) {
            console.error('Error updating project:', error);
            toast.error('Hiba a projekt frissítése során');
          }
        }}
        initialData={editingProject || undefined}
      />
    </div>
  );
};

export default MultiProjectGanttView;
